import { observer } from 'mobx-react';
import * as React from 'react';
import { invariant } from '@src/utils/error-utils';
import { autobind } from 'core-decorators';
import { getProps } from '@src/utils/link-store';
import lazyInit from '@src/utils/lazy-init';

/**
 * 创建组件 store
 *
 * e.g. const ComponentStore = createComponentStore(AnimateCss);
 *
 * @param {Function|string} Component - react class 组件
 * @param {boolean} [wrapObserver=true] - 是否包裹 observer, true: 在 getComponent 时 Component 会变成 observer(Component)
 * @returns {{new(Object=): ComponentStore, _ObserverComponent: *, prototype: ComponentStore}}
 */
export default function createComponentStore(Component, { wrapObserver = true } = {}) {
  /**
   * 组件 store, 内部针对以"_"开头的方法或属性认为私有属性将不被传入到 getComponent 组件中
   */
  const ComponentStore = class {
    /**
     * @param {BaseViewModel} vmContext
     * @param {object} [props] - 组件内部相关属性
     * @param {string[]} [requirePropNames] - 组件必要属性检查, 如: ['name'] 传入该值时会针对 name 进行非 undefined 判断处理
     */
    constructor(vmContext, props, { requirePropNames } = {}) {
      this.invariantRequirePropsCheck(requirePropNames);
      props && Object.assign(this, props);
      this.getComponent = ::this.getComponent;
      this.vmContext = vmContext;
      this.allocateLifeCycle();
    }

    /**
     * 分配生命周期
     */
    allocateLifeCycle() {
      const { componentDidMount } = this;
      componentDidMount && this.vmContext.enqueueComponentDidMount(componentDidMount);
    }

    /**
     * 获取公共的对外属性对
     * @type {function(*): T}
     */
    getProps() {
      return getProps(this);
    }

    /**
     * 进行非 undefined 的 props 检查处理
     * @param {string[]} requirePropNames - 组件必要属性检查, 如: ['name'] 传入该值时会针对 name 进行非 undefined 判断处理
     */
    invariantRequirePropsCheck(requirePropNames) {
      if (requirePropNames) {
        invariant(
          Array.isArray(requirePropNames),
          'requirePropsCheck type must be Array, typeof requirePropsCheck = ' + Object.prototype.toString.call(requirePropNames)
        );
        const invalidPropNames = requirePropNames.filter(propName => propName === undefined);
        if (invalidPropNames.length) {
          throw new Error(`the following props is required = [${invalidPropNames.join(', ')}]`);
        }
      }
    }

    /**
     * 获取 React Component, 开发者可以直接这样使用:
     * <XXXStore.Component>...</XXXStore.Component>
     * @constructor
     */
    get Component() {
      const getComponent = this.getComponent;
      return lazyInit(this, '__Component__', wrapObserver ? observer(getComponent) : getComponent);
    }

    /**
     * 获取绑定关联组件
     * @public
     *
     * @param {object} [props] - 组件属性
     */
    getComponent(props = {}) {
      return <Component {...this.getProps()} {...props} />;
    }
  };
  autobind(ComponentStore);
  return ComponentStore;
}
